// src/components/wallet/miniCard/MiniCard.tsx
import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Dialog,
  DialogContent,
  IconButton,
  Typography,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import LoginIcon from "@mui/icons-material/Login"; // <-- “go to store” icon

import { StoreCard, StoreCardGroup } from "../../../lib/types/detailsTypes";
import CardPopup from "../cardPopUp/CardPopup";
import StampCardStrip from "./stampCardStrip";
import PointCardStrip from "./pointCardStrip";
import { getStoreLogoUrl } from "../../../lib/utils/getStorageURL";

interface MiniCardProps {
  group: StoreCardGroup;
}

const MiniCard: React.FC<MiniCardProps> = ({ group }) => {
  const [open, setOpen] = useState(false);
  const [selIdx, setSelIdx] = useState<number>(0);
  const [logoUrl, setLogoUrl] = useState("/default-store-logo.png");

  /* -------------------------------------------------------------------- */
  /* Load logo once on mount (or when storeID changes)                    */
  /* -------------------------------------------------------------------- */
  useEffect(() => {
    (async () => {
      const url = await getStoreLogoUrl(group.storeID);
      setLogoUrl(url);
    })();
  }, [group.storeID]);

  /* -------------------------------------------------------------------- */
  /* Mini-card click handlers                                             */
  /* -------------------------------------------------------------------- */
  const handleProgramClick = (card: StoreCard) => {
    setSelIdx(group.cards.findIndex((c) => c.id === card.id));
    setOpen(true);
  };

  const handleClose = () => setOpen(false);

  /* -------------------------------------------------------------------- */
  /* Render                                                               */
  /* -------------------------------------------------------------------- */
  return (
    <Box
      sx={{
        width: "100%",
        mb: 2,
        display: "flex",
        justifyContent: "center",
      }}
    >
      <Card
        sx={{
          position: "relative",
          width: "100%",
          maxWidth: 600,
          borderRadius: 2,
          boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
          backgroundColor: "#fff",
          overflow: "hidden",
        }}
      >
        {/* ---------------------------------------------------------------- */}
        {/* Store header (logo, name, reward badge, jump-to-store icon)      */}
        {/* ---------------------------------------------------------------- */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 3,
            py: 2,
            backgroundColor: "#f8f9fa",
            borderBottom: "1px solid #e0e0e0",
          }}
        >
          {/* Logo + Name */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <img
              src={logoUrl}
              alt={group.storeName}
              style={{
                width: 40,
                height: 40,
                borderRadius: "50%",
                objectFit: "cover",
                border: "1px solid #e0e0e0",
                boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
              }}
              onError={(e) => {
                e.currentTarget.src = "/default-store-logo.png";
              }}
            />
            <Typography variant="h6" fontWeight={600}>
              {group.storeName}
            </Typography>
          </Box>

          {/* “Go to store” icon -- contained, right-aligned */}
          {group.storeURL && (
            <IconButton
              component={RouterLink}
              to={`/wallet/${group.storeURL}`}
              aria-label={`Visit ${group.storeName}`}
              color="primary"
              sx={{
                border: "1px solid",
                borderColor: "primary.main",
                bgcolor: "primary.main",
                color: "#fff",
                "&:hover": {
                  bgcolor: "primary.dark",
                },
              }}
            >
              <LoginIcon fontSize="small" />
            </IconButton>
          )}

          {/* Reward badge (absolutely positioned so it doesn’t bump layout) */}
          {group.anyRewardAvailable && (
            <Box
              sx={{
                position: "absolute",
                top: 53,
                left: 37 + 40,
                fontSize: "0.65rem",
                backgroundColor: "#fff8e1",
                color: "#ff9800",
                px: 0.5,
                borderRadius: 1,
                fontWeight: 600,
                border: "1px solid #ffecb3",
                whiteSpace: "nowrap",
              }}
            >
              Reward&nbsp;Available
            </Box>
          )}
        </Box>

        {/* ---------------------------------------------------------------- */}
        {/* Loyalty programs (points / stamps)                               */}
        {/* ---------------------------------------------------------------- */}
        <CardContent sx={{ p: 0, "&:last-child": { paddingBottom: 0 } }}>
          {group.cards.map((card) =>
            card.cardType === "pointCard" ? (
              <PointCardStrip
                key={card.id}
                card={card}
                onClick={() => handleProgramClick(card)}
              />
            ) : (
              <StampCardStrip
                key={card.id}
                card={card}
                onClick={() => handleProgramClick(card)}
              />
            )
          )}
        </CardContent>
      </Card>

      {/* ------------------------------------------------------------------ */}
      {/* Pop-up with full-size card view                                    */}
      {/* ------------------------------------------------------------------ */}
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogContent sx={{ p: 0 }}>
          {open && (
            <CardPopup
              open={open}
              onClose={handleClose}
              cards={group.cards}
              startIndex={selIdx}
            />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default MiniCard;
